import * as constants from "../store/Constants";
import { Recipe } from "../store/Recipe";

interface runKeywordFilterInterface {
  keyword: string;
  index: Recipe[];
  exclude: boolean;
}

const runKeywordFilter = (props: runKeywordFilterInterface) => {
  return props.index.filter((recipe) => {
    // By default (exclude == false), if the keyword is in some column, return true.
    if (!props.exclude) {
      return constants.KeywordQueryOptions.some((attribute) => {
        return recipe[attribute]
          ?.toLowerCase()
          .includes(props?.keyword?.toLowerCase());
      });
    } else {
      // exclude == true
      // If the exclude keyword is anywhere in the recipe, return false.
      return constants.KeywordQueryOptions.every((attribute) => {
        return (
          props.exclude !==
          recipe[attribute]
            ?.toLowerCase()
            .includes(props?.keyword?.toLowerCase())
        );
      });
    }
  });
};

interface runKeywordFilterRecursiveInterface {
  keywords: string[];
  index: Recipe[];
  counter: number;
  exclude: boolean;
}

const runKeywordFilterRecursive = (
  props: runKeywordFilterRecursiveInterface
): Recipe[] => {
  if (props.counter === props.keywords.length) {
    return runKeywordFilter({
      keyword: props.keywords[props.counter - 1],
      index: props.index,
      exclude: props.exclude,
    });
  }
  return runKeywordFilterRecursive({
    keywords: props.keywords,
    index: runKeywordFilter({
      keyword: props.keywords[props.counter],
      index: props.index,
      exclude: props.exclude,
    }),
    counter: props.counter + 1,
    exclude: props.exclude,
  });
};

interface runColumnFilterInterface {
  column: string;
  tag: string;
  exclude: boolean;
  index: Recipe[];
}

const runColumnFilter = (props: runColumnFilterInterface) => {
  return props.index.filter((recipe) => {
    return (
      props.exclude !==
      recipe[props.column]?.toLowerCase().includes(props?.tag?.toLowerCase())
    );
  });
};

interface runColumnFilterRecursiveInterface {
  column: string;
  tags: string[];
  index: Recipe[];
  exclude: boolean;
  counter: number;
}

const runColumnFilterRecursive = (
  props: runColumnFilterRecursiveInterface
): Recipe[] => {
  if (props.counter === props.tags.length) {
    return runColumnFilter({
      column: props.column,
      tag: props.tags[props.counter - 1],
      index: props.index,
      exclude: props.exclude,
    });
  }
  return runColumnFilterRecursive({
    column: props.column,
    tags: props.tags,
    index: runColumnFilter({
      column: props.column,
      tag: props.tags[props.counter],
      index: props.index,
      exclude: props.exclude,
    }),
    counter: props.counter + 1,
    exclude: props.exclude,
  });
};

interface runOrColumnFilterInterface {
  column: string;
  tags: string[];
  index: Recipe[];
}

const runOrColumnFilter = (props: runOrColumnFilterInterface) => {
  return props.index.filter((recipe) => {
    return props.tags.some((attribute) => {
      return recipe[props.column]
        ?.toLowerCase()
        .includes(attribute?.toLowerCase());
    });
  });
};

interface filterByTimeInterface {
  minTime: number;
  maxTime: number;
  index: Recipe[];
}

const filterByTime = (props: filterByTimeInterface) => {
  return props.index.filter((recipe) => {
    const timeFloat = parseFloat(recipe[constants.Time]);
    if (timeFloat) {
      const time = +parseFloat(recipe[constants.Time]);
      return time >= props.minTime && time <= props.maxTime;
    }
    return true;
  });
};

interface filterByVegetarianInterface {
  vegetarianState: string;
  index: Recipe[];
}

const filterByVegetarian = (props: filterByVegetarianInterface) => {
  if (props.vegetarianState === "all") {
    return props.index;
  } else if (props.vegetarianState === "yes") {
    return props.index.filter((recipe) => {
      return (
        recipe["Vegetarian"]?.toLowerCase() === "y" ||
        recipe["Vegetarian"]?.toLowerCase() === "m"
      );
    });
  } else {
    return props.index.filter((recipe) => {
      return recipe["Vegetarian"]?.toLowerCase() === "n";
    });
  }
};

interface filterIndexInterface {
  query: URLSearchParams;
  index: Recipe[];
}

export const filterIndex = (props: filterIndexInterface) => {
  // Basic input validation.
  if (props.index.length < 1) {
    return props.index;
  }

  // Do keyword search.
  var keywordFilterResult = props.index;
  const keywords = props.query.getAll(constants.Keyword);
  if (keywords.length > 0) {
    keywordFilterResult = runKeywordFilterRecursive({
      keywords: keywords,
      index: props.index,
      counter: 0,
      exclude: false,
    });
  }

  // Do advanced search.
  var excludeKeywordFilterResult = keywordFilterResult;
  var listsFilterResult = excludeKeywordFilterResult;
  var categoryFilterResult = listsFilterResult;
  var cuisineFilterResult = categoryFilterResult;
  var formFilterResult = cuisineFilterResult;
  var timeFilterResult = formFilterResult;
  var methodFilterResult = timeFilterResult;
  var vegetarianFilterResult = methodFilterResult;
  var ingredientFilterResult = vegetarianFilterResult;
  var excludeIngredientFilterResult = ingredientFilterResult;

  const excludeKeywords = props.query.getAll(constants.ExcludeKeyword);
  if (excludeKeywords.length > 0) {
    excludeKeywordFilterResult = runKeywordFilterRecursive({
      keywords: excludeKeywords,
      index: keywordFilterResult,
      counter: 0,
      exclude: true,
    });
  } else {
    excludeKeywordFilterResult = keywordFilterResult;
  }

  const lists = props.query.getAll(constants.Lists);
  if (lists.length > 0) {
    listsFilterResult = runOrColumnFilter({
      column: constants.Lists,
      tags: lists,
      index: excludeKeywordFilterResult,
    });
  } else {
    listsFilterResult = excludeKeywordFilterResult;
  }

  const categories = props.query.getAll(constants.Category);
  if (categories.length > 0) {
    categoryFilterResult = runOrColumnFilter({
      column: constants.Category,
      tags: categories,
      index: listsFilterResult,
    });
  } else {
    categoryFilterResult = listsFilterResult;
  }

  const cuisines = props.query.getAll(constants.Cuisine);
  if (cuisines.length > 0) {
    cuisineFilterResult = runOrColumnFilter({
      column: constants.Cuisine,
      tags: cuisines,
      index: categoryFilterResult,
    });
  } else {
    cuisineFilterResult = categoryFilterResult;
  }

  const forms = props.query.getAll(constants.Form);
  if (forms.length > 0) {
    formFilterResult = runOrColumnFilter({
      column: constants.Form,
      tags: forms,
      index: cuisineFilterResult,
    });
  } else {
    formFilterResult = cuisineFilterResult;
  }

  // Get time query
  const minTimeQuery = props.query.get(constants.MinTime);
  const maxTimeQuery = props.query.get(constants.MaxTime);
  var minTimeState = 0;
  var maxTimeState = 24; // hopefully no one enters a recipe with >24 hours
  if (minTimeQuery !== null && maxTimeQuery !== null) {
    minTimeState = +minTimeQuery;
    maxTimeState = +maxTimeQuery;
  }
  // Filter by time query.
  timeFilterResult = filterByTime({
    minTime: minTimeState,
    maxTime: maxTimeState,
    index: formFilterResult,
  });

  const methods = props.query.getAll(constants.Method);
  if (methods.length > 0) {
    methodFilterResult = runOrColumnFilter({
      column: constants.Method,
      tags: methods,
      index: timeFilterResult,
    });
  } else {
    methodFilterResult = timeFilterResult;
  }

  // Get vegetarian query.
  const vegetarianQuery = props.query.get(constants.Vegetarian);
  var vegetarianQueryState = "all";
  if (vegetarianQuery !== null) {
    vegetarianQueryState = vegetarianQuery;
  }
  vegetarianFilterResult = filterByVegetarian({
    vegetarianState: vegetarianQueryState,
    index: methodFilterResult,
  });

  const ingredients = props.query.getAll(constants.Ingredients);
  if (ingredients.length > 0) {
    ingredientFilterResult = runOrColumnFilter({
      column: constants.Ingredients,
      tags: ingredients,
      index: vegetarianFilterResult,
    });
  } else {
    ingredientFilterResult = vegetarianFilterResult;
  }

  const excludeIngredients = props.query.getAll(constants.ExcludeIngredient);
  if (excludeIngredients.length > 0) {
    excludeIngredientFilterResult = runColumnFilterRecursive({
      column: constants.Ingredients,
      tags: excludeIngredients,
      index: ingredientFilterResult,
      counter: 0,
      exclude: true,
    });
  } else {
    excludeIngredientFilterResult = ingredientFilterResult;
  }

  return excludeIngredientFilterResult;
};
